import React, { useState, useEffect } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Car,
  User,
  Hash,
  Calendar,
  Users,
  CheckCircle,
  MapPin,
  Fuel,
  Wrench,
  FileText,
  Edit,
  Trash2,
  RefreshCw,
  Loader2,
  Smartphone,
  Activity,
  Shield,
  AlertTriangle,
} from 'lucide-react';

import { BASE_URL } from '../../Screens/Settings/DASHBOARD_SETTINGS';
import { Button } from '../Global/UI/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../Global/UI/Dialog';
import { Input } from '../Global/UI/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Global/UI/Select';
import { Badge } from '../Global/UI/Badge';

const DriverFullDetails = () => {
  const history = useHistory();
  const { driverId } = useParams();

  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingData, setTrackingData] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    if (driverId) {
      fetchDriver();
    }
  }, [driverId]);

  useEffect(() => {
    if (driver) {
      setEditData({
        username: driver.username || '',
        registrationNumber: driver.registrationNumber || '',
        vehicleType: driver.vehicleType || 'ambulance',
        make: driver.make || '',
        model: driver.model || '',
        year: driver.year || '',
        capacity: driver.capacity || 1,
        status: driver.status || 'available',
        currentLocation: driver.currentLocation || '',
        mileage: driver.mileage || 0,
        fuelType: driver.fuelType || '',
        lastServiceDate: driver.lastServiceDate ? new Date(driver.lastServiceDate).toISOString().split('T')[0] : '',
        nextServiceDate: driver.nextServiceDate ? new Date(driver.nextServiceDate).toISOString().split('T')[0] : '',
        insuranceExpiry: driver.insuranceExpiry ? new Date(driver.insuranceExpiry).toISOString().split('T')[0] : '',
        motExpiry: driver.motExpiry ? new Date(driver.motExpiry).toISOString().split('T')[0] : '',
        notes: driver.notes || '',
      });
    }
  }, [driver]);

  const fetchDriver = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/drivers/driver/${driverId}`);
      if (response.data.success) {
        setDriver(response.data.driver);
      } else {
        toast.error('Failed to fetch driver.');
      }
    } catch (error) {
      toast.error('Error fetching driver.');
      console.error('Error fetching driver:', error);
    } finally {
      setLoading(false);
    }
  };



  const [editData, setEditData] = useState({
    username: '',
    registrationNumber: '',
    vehicleType: 'ambulance',
    make: '',
    model: '',
    year: '',
    capacity: 1,
    status: 'available',
    currentLocation: '',
    mileage: 0,
    fuelType: '',
    lastServiceDate: '',
    nextServiceDate: '',
    insuranceExpiry: '',
    motExpiry: '',
    notes: '',
  });

  const updateDriver = async () => {
    setIsUpdating(true);
    try {
      // Convert date strings to Date objects for the API
      const updateData = {
        ...editData,
        lastServiceDate: editData.lastServiceDate ? new Date(editData.lastServiceDate) : null,
        nextServiceDate: editData.nextServiceDate ? new Date(editData.nextServiceDate) : null,
        insuranceExpiry: editData.insuranceExpiry ? new Date(editData.insuranceExpiry) : null,
        motExpiry: editData.motExpiry ? new Date(editData.motExpiry) : null,
      };
      const response = await axios.put(`${BASE_URL}/api/drivers/update-driver-full/${driver._id}`, updateData);
      if (response.data.success) {
        toast.success('Driver updated successfully!');
        setIsEditing(false);
        // Refresh the data
        fetchDriver();
      } else {
        toast.error('Failed to update driver.');
      }
    } catch (error) {
      toast.error('Error updating driver.');
      console.error('Error updating driver:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteDriver = async () => {
    setIsDeleting(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/drivers/delete`, {
        ids: [driver._id],
      });
      if (response.data.success) {
        toast.success('Driver deleted successfully!');
        history.push('/drivers');
      } else {
        toast.error('Failed to delete driver.');
      }
    } catch (error) {
      toast.error('Error deleting driver.');
      console.error('Error deleting driver:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <div className="p-10">Loading driver details...</div>;
  }

  if (!driver) {
    return <div className="p-10">Driver not found.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto py-10">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Ambulance Details
          </h1>
          <p className="text-gray-600 text-sm">
            {driver.username} - {driver.registrationNumber || 'No registration'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isEditing ? (
            <>
              <Button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={updateDriver}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                {isUpdating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Save
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setEditData({
                    username: driver?.username || '',
                    registrationNumber: driver?.registrationNumber || '',
                    vehicleType: driver?.vehicleType || 'ambulance',
                    make: driver?.make || '',
                    model: driver?.model || '',
                    year: driver?.year || '',
                    capacity: driver?.capacity || 1,
                    status: driver?.status || 'available',
                    currentLocation: driver?.currentLocation || '',
                    mileage: driver?.mileage || 0,
                    fuelType: driver?.fuelType || '',
                    lastServiceDate: driver?.lastServiceDate ? new Date(driver.lastServiceDate).toISOString().split('T')[0] : '',
                    nextServiceDate: driver?.nextServiceDate ? new Date(driver.nextServiceDate).toISOString().split('T')[0] : '',
                    insuranceExpiry: driver?.insuranceExpiry ? new Date(driver.insuranceExpiry).toISOString().split('T')[0] : '',
                    motExpiry: driver?.motExpiry ? new Date(driver.motExpiry).toISOString().split('T')[0] : '',
                    notes: driver?.notes || '',
                  });
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-6">
        <Badge
          style={
            driver.status === 'available'
              ? { backgroundColor: '#10B981', color: 'white', border: 'none' }
              : driver.status === 'in-use'
              ? { backgroundColor: '#3B82F6', color: 'white', border: 'none' }
              : driver.status === 'maintenance'
              ? { backgroundColor: '#F59E0B', color: 'white', border: 'none' }
              : { backgroundColor: '#EF4444', color: 'white', border: 'none' }
          }
        >
          {driver.status ? driver.status.charAt(0).toUpperCase() + driver.status.slice(1) : "Unknown"}
        </Badge>
      </div>

      {/* Details */}
      <div className="space-y-8">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <User className="h-4 w-4" />
                Username
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.username : driver.username}
                onChange={(e) => setEditData({ ...editData, username: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Hash className="h-4 w-4" />
                Driver ID
              </label>
              <Input readOnly value={driver._id} />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Car className="h-4 w-4" />
                Registration Number
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.registrationNumber : (driver.registrationNumber || "N/A")}
                onChange={(e) => setEditData({ ...editData, registrationNumber: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Vehicle Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Vehicle Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Car className="h-4 w-4" />
                Vehicle Type
              </label>
              {isEditing ? (
                <Select
                  value={editData.vehicleType}
                  onValueChange={(value) => setEditData({ ...editData, vehicleType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ambulance">Ambulance</SelectItem>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input readOnly value={driver.vehicleType ? driver.vehicleType.charAt(0).toUpperCase() + driver.vehicleType.slice(1) : "N/A"} />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Car className="h-4 w-4" />
                Make
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.make : (driver.make || "N/A")}
                onChange={(e) => setEditData({ ...editData, make: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Car className="h-4 w-4" />
                Model
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.model : (driver.model || "N/A")}
                onChange={(e) => setEditData({ ...editData, model: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="h-4 w-4" />
                Year
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.year : (driver.year || "N/A")}
                onChange={(e) => setEditData({ ...editData, year: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4" />
                Capacity
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.capacity : (driver.capacity || "N/A")}
                onChange={(e) => setEditData({ ...editData, capacity: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Fuel className="h-4 w-4" />
                Fuel Type
              </label>
              {isEditing ? (
                <Select
                  value={editData.fuelType}
                  onValueChange={(value) => setEditData({ ...editData, fuelType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="petrol">Petrol</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="electric">Electric</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input readOnly value={driver.fuelType ? driver.fuelType.charAt(0).toUpperCase() + driver.fuelType.slice(1) : "N/A"} />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Wrench className="h-4 w-4" />
                Mileage
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.mileage : (driver.mileage || "N/A")}
                onChange={(e) => setEditData({ ...editData, mileage: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Status & Location */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Status & Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle className="h-4 w-4" />
                Status
              </label>
              {isEditing ? (
                <Select
                  value={editData.status}
                  onValueChange={(value) => setEditData({ ...editData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="in-use">In Use</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="out-of-service">Out of Service</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge
                  style={
                    driver.status === 'available'
                      ? { backgroundColor: '#4CAF50', color: 'white', border: 'none' }
                      : driver.status === 'in-use'
                      ? { backgroundColor: '#2196F3', color: 'white', border: 'none' }
                      : driver.status === 'maintenance'
                      ? { backgroundColor: '#FF9800', color: 'white', border: 'none' }
                      : { backgroundColor: '#F44336', color: 'white', border: 'none' }
                  }
                >
                  {driver.status || "Unknown"}
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <MapPin className="h-4 w-4" />
                Current Location
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.currentLocation : (driver.currentLocation || "N/A")}
                onChange={(e) => setEditData({ ...editData, currentLocation: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Maintenance */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Maintenance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="h-4 w-4" />
                Last Service Date
              </label>
              {isEditing ? (
                <Input
                  type="date"
                  value={editData.lastServiceDate}
                  onChange={(e) => setEditData({ ...editData, lastServiceDate: e.target.value })}
                />
              ) : (
                <Input
                  readOnly
                  value={
                    driver.lastServiceDate ? new Date(driver.lastServiceDate).toLocaleDateString('en-GB') : "N/A"
                  }
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="h-4 w-4" />
                Next Service Date
              </label>
              {isEditing ? (
                <Input
                  type="date"
                  value={editData.nextServiceDate}
                  onChange={(e) => setEditData({ ...editData, nextServiceDate: e.target.value })}
                />
              ) : (
                <Input
                  readOnly
                  value={
                    driver.nextServiceDate ? new Date(driver.nextServiceDate).toLocaleDateString('en-GB') : "N/A"
                  }
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Shield className="h-4 w-4" />
                Insurance Expiry
              </label>
              {isEditing ? (
                <Input
                  type="date"
                  value={editData.insuranceExpiry}
                  onChange={(e) => setEditData({ ...editData, insuranceExpiry: e.target.value })}
                />
              ) : (
                <Input
                  readOnly
                  value={
                    driver.insuranceExpiry ? new Date(driver.insuranceExpiry).toLocaleDateString('en-GB') : "N/A"
                  }
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4" />
                MOT Expiry
              </label>
              {isEditing ? (
                <Input
                  type="date"
                  value={editData.motExpiry}
                  onChange={(e) => setEditData({ ...editData, motExpiry: e.target.value })}
                />
              ) : (
                <Input
                  readOnly
                  value={
                    driver.motExpiry ? new Date(driver.motExpiry).toLocaleDateString('en-GB') : "N/A"
                  }
                />
              )}
            </div>
          </div>
        </div>

        {/* Other Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Other Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Smartphone className="h-4 w-4" />
                Device Tokens
              </label>
              <div className="text-sm">
                {driver.deviceTokens && driver.deviceTokens.length > 0
                  ? driver.deviceTokens.join(", ")
                  : "N/A"}
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Activity className="h-4 w-4" />
                Logs Count
              </label>
              <Input readOnly value={driver.logs ? driver.logs.length : 0} />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4" />
                Assigned Staff
              </label>
              <div className="text-sm">
                {driver.assignedStaff && driver.assignedStaff.length > 0
                  ? driver.assignedStaff.join(", ")
                  : "N/A"}
              </div>
            </div>
            <div className="space-y-2 col-span-1 md:col-span-2 lg:col-span-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Wrench className="h-4 w-4" />
                Equipment
              </label>
              <div className="flex flex-wrap gap-2">
                {driver.equipment && driver.equipment.length > 0 ? (
                  driver.equipment.map((item, index) => (
                    <Badge key={index} variant="outline">
                      {item}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">N/A</span>
                )}
              </div>
            </div>
            <div className="space-y-2 col-span-1 md:col-span-2 lg:col-span-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <FileText className="h-4 w-4" />
                Notes
              </label>
              <Input
                readOnly={!isEditing}
                value={isEditing ? editData.notes : (driver.notes || "N/A")}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Maintenance History */}
        {driver.maintenanceHistory && driver.maintenanceHistory.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Maintenance History</h3>
            <div className="space-y-2">
              {driver.maintenanceHistory.map((item, index) => (
                <div key={index} className="border rounded p-4">
                  <div className="flex justify-between">
                    <span className="font-medium">{item.description}</span>
                    <span className="text-sm text-gray-500">
                      {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Cost: £{item.cost || 0}, Mileage: {item.mileage || 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>


      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the ambulance for {driver.username}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteDriver} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverFullDetails;
